import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import useTermsStore from '../../store/useTermsStore';
import toast from 'react-hot-toast';

const CookieConsentModal = ({ isOpen, onClose, onAccept }) => {
  const { acceptTerms } = useTermsStore();
  const [isAccepted, setIsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleAccept = async () => {
    if (!isAccepted) {
      toast.error('You must accept cookies to continue using this platform');
      return;
    }

    setIsLoading(true);
    try {
      // Accept cookies and terms in one action
      acceptTerms();
      toast.success('Cookies accepted successfully');
      onAccept && onAccept();
      onClose();
    } catch (error) {
      toast.error('Failed to accept cookies. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = () => {
    toast.error('You must accept cookies to use this platform');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 mt-16 p-4">
      <div className=" rounded-2xl max-w-3xl w-full shadow-2xl border border-gray-100">
        {/* Header */}
        <div className="bg-[#0E1696] px-6 py-2 rounded-t-2xl">
          <div className="flex items-center space-x-4">
            <div>
              <img src="./cookies.svg" alt="logo" className="w-8 h-8 object-contain" />
            </div>
            <div>
              <h2 className="text-lg font-bold !text-white">Cookies Consent</h2>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Description */}
          <div className="mb-2">
            <p className=" leading-relaxed font-normal text-xs">
              We use cookies to provide essential platform functionality, security, and to remember your preferences. 
              By continuing, you agree to our use of cookies and acknowledge the risks of crypto trading.
            </p>
          </div>

          {/* Cookie Types */}
          <div className=" rounded-lg p-4 ">
            <h3 className="font-bold !text-white mb-3 text-xs">What we use cookies for:</h3>
            <div className="grid grid-cols-1 gap-2 text-xs text-white">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 !bg-white rounded-full"></div>
                <span className="font-medium !text-white">Essential platform functionality</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 !bg-white rounded-full"></div>
                <span className="font-medium !text-white">Security and authentication</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 !bg-white rounded-full"></div>
                <span className="font-medium !text-white">Remembering your preferences</span>
              </div>
            </div>
          </div>

          {/* Risk Disclaimer */}
          <div className=" rounded-lg px-2 mb-2">
            <div className="flex items-start space-x-3">
             
              <div>
                <h4 className="font-semibold !text-red-400 text-sm mb-1">Important Risk Warning</h4>
                <p className="text-xs !text-white leading-relaxed">
                  Crypto trading involves substantial risk. We are <strong>NOT responsible</strong> for any losses. 
                
                </p>
              </div>
            </div>
          </div>

          {/* Acceptance Checkbox */}
          <div className=" px-2 mb-2">
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="accept-cookies"
                checked={isAccepted}
                onChange={(e) => setIsAccepted(e.target.checked)}
                className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-400 rounded"
              />
              <label htmlFor="accept-cookies" className="text-base text-white leading-relaxed font-medium">
                I understand the risks and agree to use this platform at my own risk. I accept cookies and the{' '}
                <a href="/terms" target="_blank" className="text-white  underline font-bold">
                  Terms of Use
                </a>
                .
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleReject}
              className="flex-1 px-5 py-2 bg-gray-700 !text-white font-medium rounded-xl hover:bg-gray-200 hover:!text-black transition-all duration-200 border border-gray-200"
            >
              Reject & Exit
            </button>
            <button
              onClick={handleAccept}
              disabled={!isAccepted || isLoading}
              className="flex-1 px-5 py-2 !bg-[#0E1696] !text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:bg-gray-200 disabled:!text-white disabled:cursor-not-allowed transition-all duration-200 shadow-lg flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                'Accept & Continue'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentModal;
